import { Box } from '@mui/material';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { Redirect, Route, Switch } from 'react-router-dom';
import { delayWhen, Observable, of, ReplaySubject } from 'rxjs';

import { NotFound } from 'app/components/not-found';
import { SeamPattern } from 'app/model/seam-pattern';
import { SeamRequest } from 'app/model/seam-request';
import { RootState } from 'app/store';

import { Details } from './details/details';
import { Labels } from './labels';
import { PatternSplitEditor } from './pattern-split-editor';
import { Patterns } from './patterns';
import { DataQuality } from './qa/data-quality';
import { RequestMenu } from './request-menu';
import { SeamRequestHistory } from './seam-request-history';
import {
    loadSeamPatterns,
    loadSeamRequest,
    selectRequest,
    selectRequestPatterns,
    selectSelectedRequest,
    setSeamPatterns,
} from '../slice';

interface RequestRouterProps extends RouteComponentProps<{ id: string }> {
    // redux props
    patterns?: SeamPattern[];
    selectedRequest?: SeamRequest;

    // redux actions
    selectRequest: (request?: SeamRequest) => void;
    loadSeamRequest: (id: string) => Observable<SeamRequest>;
    loadSeamPatterns: (request: SeamRequest) => Observable<SeamPattern[]>;
    setSeamPatterns: (patterns?: SeamPattern[]) => void;
}

class RequestRouter extends React.Component<RequestRouterProps> {
    public componentDidMount() {
        const id = this.props.match.params.id;
        let notifier: Observable<unknown> = of(1);

        if (!this.props.selectedRequest || this.props.selectedRequest.id !== id) {
            const notifierSubject = new ReplaySubject(1);
            // we are going to load request, replace notifier with one which will complete when this loading is done.
            notifier = notifierSubject.asObservable();

            this.props.selectRequest();
            this.props.loadSeamRequest(id).subscribe((value) => {
                this.props.selectRequest(value);
                notifierSubject.next(1);
                notifierSubject.complete();
            });
        }

        if (undefined === this.props.patterns) {
            this.props
                .loadSeamPatterns({ id } as SeamRequest)
                .pipe(delayWhen(() => notifier))
                .subscribe((value) => {
                    // we must wait for loadSeamRequests to complete before setting this
                    this.props.setSeamPatterns(value);
                });
        }
    }

    public render() {
        const request = this.props.selectedRequest;

        if (!request) {
            return '';
        }

        const requestId = this.props.match.params.id;

        return (
            <Box display='flex' flexDirection='row' flex='1 1 auto'>
                <Switch>
                    <Route exact path='/seam/request/:id/patterns/:pattern'>
                        <Box flexGrow={1} className='p-2'>
                            <PatternSplitEditor
                                request={request}
                                patterns={this.props.patterns || []}
                                store={this.props.selectedRequest?.store}
                            />
                        </Box>
                    </Route>
                    <Route path=''>
                        <Box display='flex' flexDirection='column' width='14rem'>
                            <RequestMenu selectedRequest={request} requestId={requestId} />
                        </Box>
                        <Box flexGrow={1} className='p-1' display='flex' flexDirection='column'>
                            <Switch>
                                <Redirect exact from='/seam/request/:id/' to='/seam/request/:id/details' />
                                <Route path='/seam/request/:id/details'>
                                    <Details request={request} patterns={this.props.patterns} />
                                </Route>
                                <Route exact path='/seam/request/:id/patterns'>
                                    <Patterns request={request} patterns={this.props.patterns} />
                                </Route>
                                <Route path='/seam/request/:id/qa'>
                                    <DataQuality request={request} />
                                </Route>
                                <Route path='/seam/request/:id/labels'>
                                    <Labels />
                                </Route>
                                <Route path='/seam/request/:id/history'>
                                    <SeamRequestHistory request={request} />
                                </Route>
                                <Route path=''>
                                    <NotFound />
                                </Route>
                            </Switch>
                        </Box>
                    </Route>
                </Switch>
            </Box>
        );
    }
}

const mapStateToProps = (state: RootState) => ({
    patterns: selectRequestPatterns(state),
    selectedRequest: selectSelectedRequest(state),
});
const thunks = {
    selectRequest: selectRequest as ActionCreatorWithPayload<SeamRequest | undefined>,
    loadSeamRequest,
    loadSeamPatterns,
    setSeamPatterns: setSeamPatterns as ActionCreatorWithPayload<SeamPattern[] | undefined>,
};
const connected = connect(mapStateToProps, thunks)(withRouter(RequestRouter));

export { connected as RequestRouter };
